import { themeDemoCatalog } from '../../themes/demoCatalogs.js';
/**
 * Whether this render may fall back to DEMO content. FALSE on the authoritative storefront
 * ({@link RenderContext.catalogResolved} — set only by the real SSR / island hosts, never by the builder):
 * a real customer must see the store's OWN data or nothing at all, never fabricated sample products,
 * collections or articles. TRUE in the builder canvas and in offline/demo previews, where a freshly
 * inserted section has to render something for the seller to design against.
 *
 * This is the single gate every demo fallback in the section library goes through.
 */
export function allowDemo(context) {
    return context.catalogResolved !== true;
}
/**
 * feature 0023 — decide how a picker ref should render, from the doc-ref presence + whether the host fully
 * resolved catalog refs ({@link RenderContext.catalogResolved}, true only on the authoritative storefront SSR):
 *  - nothing picked           → `'demo'` in the builder (show the sample layout), `'hidden'` in live (an
 *                               unbound section has no data of its own ⇒ never fabricate one for a customer)
 *  - picked + resolved        → `'real'`
 *  - picked + unresolved      → `'hidden'` on the authoritative storefront (DRAFT/deleted ⇒ hide in live),
 *                               `'demo'` in the builder (not ref-resolved ⇒ never vanish a published section).
 * So a real customer is never served fabricated demo data, and a bound section never blanks in the builder.
 */
export function refRender(context, refId, resolved) {
    if (resolved != null && refId)
        return 'real';
    return allowDemo(context) ? 'demo' : 'hidden';
}
/**
 * Demo photography is GONE from the house catalog (founder direction 2026-09-16, extending the
 * 2026-07-05 call on {@link demoProducts}): sample content must read as "your photo goes here", never as
 * someone else's shop. Every house demo product AND collection now carries no media at all, so the cards
 * draw {@link PlaceholderMedia} — in the Add-section picker preview, in the canvas and offline alike.
 * A theme's own demo catalog keeps its (owned, generated) photography; see {@link demoCollectionsFor} for
 * the one place that strips it too.
 */
/**
 * The built-in sample product — title, price, compareAt, 2 variants, NO images (build brief).
 * It ships with an EMPTY `images` array on purpose: media blocks/sections that fall back to this demo
 * product render the neutral scenic placeholder (PlaceholderMedia, variant B) instead of stock apparel
 * photos, so a fresh store never shows someone else's product shots. Sellers upload their own media.
 */
export function demoProduct(currency) {
    return {
        id: 'demo-product',
        name: 'Linen Weekend Shirt',
        slug: 'linen-weekend-shirt',
        description: 'A breathable, pre-washed linen shirt cut for everyday wear. Soft from the first wear, ' +
            'built to last seasons.',
        price: { amount: 24900, currency },
        compareAtPrice: { amount: 34900, currency },
        images: [],
        inventory: { tracked: false, quantity: null },
        seo: { metaTitle: null, metaDescription: null },
        status: 'active',
        // A real OPTION (Color) so the demo product page shows a genuine variant picker — the picker is driven
        // by the product's options, NOT by the product itself (a simple product has none → no picker).
        options: [
            {
                name: 'Color',
                values: [
                    { label: 'Sand', swatch: '#d8c7b0' },
                    { label: 'Olive', swatch: '#6b6a4a' },
                ],
            },
        ],
    };
}
/**
 * Build the buy-box picker variants from a product's FIRST option (Sand/Olive…), Shopify-style: a product
 * with options shows those values as the picker; a SIMPLE product with no options shows NO picker at all
 * (returns []), instead of a meaningless single "variant" labelled with the product name. (Multi-option
 * products still render only the first option here — the full multi-option matrix is the deferred rich model.)
 */
export function variantsFromOptions(product) {
    const option = product.options?.[0];
    if (!option || option.values.length === 0)
        return [];
    return option.values.map((v, i) => ({
        id: `${product.id}:${i}`,
        label: v.label,
        swatch: v.swatch ?? null,
        photo: v.imageUrl ?? null,
        available: true,
    }));
}
/**
 * Buy-box picker/submit variants: prefer the product's REAL variants (real backend UUIDs) so a COD submit
 * sends a checkout-valid id — never the synthetic `${id}:${i}`. Falls back to the synthetic option-derived
 * list ONLY when real variants are absent OR empty (builder / demo / grid carry no `variants` field). A
 * simple product's single default variant (empty label) is kept so `variants[0].id` is its real UUID; the
 * picker hides itself for a lone unlabeled variant. Swatch is best-effort: a single-option product's variant
 * label matches an option value → reuse its swatch (colour picker preserved); multi-option → a text pill.
 */
export function pickerVariants(product) {
    const real = product.variants;
    if (real && real.length > 0) {
        const soleOption = product.options?.length === 1 ? product.options[0] : undefined;
        return real.map((v) => {
            const soleValue = soleOption?.values.find((ov) => ov.label === v.label);
            return {
                id: v.id,
                label: v.label,
                swatch: soleValue?.swatch ?? null,
                photo: soleValue?.imageUrl ?? null,
                available: v.available,
            };
        });
    }
    return variantsFromOptions(product);
}
/**
 * A small grid of DEMO products for the multi-product sections (featured-collection, related-products,
 * collection grids, the demo cart). Same `isDemo` contract as {@link demoProduct}: rendered ONLY when no
 * real product refs resolve (builder preview / offline), so a freshly-inserted grid section is immediately
 * useful. Prices follow `context.store.currency` so `context.formatMoney` formats them.
 *
 * Covers are intentionally left EMPTY (`images: []`) — founder direction (2026-07-05): the demo grid must
 * NOT ship stock lifestyle photos. Every card renders our neutral scenic placeholder (PlaceholderMedia
 * variant 'b', the founder's default) instead, reading as "your product photo goes here" rather than
 * unrelated demo imagery the seller must hunt down and replace. Real covers appear as soon as a picked /
 * route-bound collection resolves products with images. Titles, prices, compare-at, ratings, colour options
 * and `custom-badge-N` tags stay so the badge / swatch / price controls still have demo content to style.
 */
export function demoProducts(currency, presetId) {
    // Idem : le catalogue du thème actif prime sur la garde-robe maison (voir demoCollections).
    const themed = themeDemoCatalog(presetId)?.products(currency);
    if (themed?.length)
        return themed;
    const make = (id, name, price, compareAt, vendor, rating, colors, tags = []) => ({
        id,
        name,
        slug: id,
        description: '',
        price: { amount: price, currency },
        compareAtPrice: compareAt ? { amount: compareAt, currency } : null,
        images: [],
        inventory: { tracked: false, quantity: null },
        seo: { metaTitle: null, metaDescription: null },
        status: 'active',
        vendor,
        rating,
        options: [{ name: 'Color', values: colors }],
        tags,
    });
    // Tags carry `custom-badge-N` markers so the card's "Custom" badge mode has demo content to show
    // (Shrine reads the Nth entry of the section's custom-badges list). The tee is also on sale.
    return [
        make('demo-tee', 'Cotton Crew Tee', 14900, 19900, 'Aquilix Basics', { value: 4.5, count: 128 }, [
            { label: 'White', swatch: '#f4f4f5' },
            { label: 'Black', swatch: '#1a1a1a' },
            { label: 'Olive', swatch: '#6b6a4a' },
        ], ['custom-badge-2']),
        make('demo-cap', 'Washed Canvas Cap', 8900, null, 'Field & Co', { value: 4, count: 64 }, [
            { label: 'Sand', swatch: '#d8c7b0' },
            { label: 'Black', swatch: '#1a1a1a' },
        ], ['custom-badge-1']),
        make('demo-tote', 'Heavy Canvas Tote', 12900, 16900, 'Heavy Goods', { value: 5, count: 36 }, [
            { label: 'Natural', swatch: '#cbb893' },
            { label: 'Black', swatch: '#1a1a1a' },
        ]),
        make('demo-socks', 'Ribbed Wool Socks', 5900, null, 'Warmth Lab', { value: 4.5, count: 92 }, [
            { label: 'Charcoal', swatch: '#3a3a3a' },
            { label: 'Cream', swatch: '#f0e9d8' },
        ]),
    ];
}
/**
 * A small set of DEMO collections for the collection-list section. Same `isDemo`-style contract as
 * {@link demoProducts}: rendered ONLY when no real collection refs resolve (builder preview / offline), so
 * a freshly-inserted collection grid is immediately useful in the editor. Each carries a name + slug and NO
 * cover image, so every card draws the neutral placeholder. The `currency` arg mirrors {@link
 * demoProducts}'s signature for call-site symmetry (collections carry no price, so it is currently unused).
 */
export function demoCollections(currency, presetId) {
    // Le thème actif impose SON étalage quand il en a un : ouvrir Bloom sur une boutique vide affichait
    // sinon la boutique de vêtements maison (« Nouveautés / Meilleures ventes / Promotions » sur des
    // photos de portants) au milieu d'un thème beauté.
    const themed = themeDemoCatalog(presetId)?.collections(currency);
    if (themed?.length)
        return themed;
    const make = (id, name, productCount) => ({
        id,
        name,
        slug: id,
        description: '',
        // NO cover (2026-09-16) — exactly like `demoProducts`' empty `images`: the card draws the neutral
        // scenic placeholder instead of a stock photo of a clothes rail the seller never chose.
        coverImage: null,
        productCount,
    });
    return [
        make('demo-c-new', 'Nouveautés', 24),
        make('demo-c-best', 'Meilleures ventes', 18),
        make('demo-c-sale', 'Promotions', 12),
    ];
}
/**
 * Whether THIS render may show demo PHOTOGRAPHY at all ({@link RenderContext.demoMedia}). The house demo
 * catalog carries none by construction, but a theme ships its own (owned, generated) demo photos — and the
 * Add-section picker's preview pane must show every component with the neutral placeholder, never with a
 * photo the seller would have to hunt down and replace after inserting it. The builder's preview is the one
 * host that sets `demoMedia: false`; the canvas and the storefront leave it unset.
 */
function demoMediaAllowed(context) {
    return context.demoMedia !== false;
}
/** The demo products for THIS context — theme catalog included, media stripped when the host forbids it. */
export function demoProductsFor(context) {
    const list = demoProducts(context.store.currency, context.themeSettings?.presetId);
    if (demoMediaAllowed(context))
        return list;
    return list.map((p) => (p.images.length ? { ...p, images: [] } : p));
}
/** The demo collections for THIS context — same contract as {@link demoProductsFor}. */
export function demoCollectionsFor(context) {
    const list = demoCollections(context.store.currency, context.themeSettings?.presetId);
    if (demoMediaAllowed(context))
        return list;
    return list.map((c) => (c.coverImage ? { ...c, coverImage: null } : c));
}
/**
 * Resolve the product a section should render: the route-bound product when present, else the demo. The
 * picker variants are derived from the product's OPTIONS ({@link variantsFromOptions}) — so a product with
 * a Color/Size option shows that picker and a SIMPLE product (no options) shows NO picker, instead of a
 * meaningless single pill labelled with the product name.
 *
 * FALLBACK: on a product route with NO bound product (the builder opening the DEFAULT product document,
 * an offline preview), we fall back to the demo product — which carries a Color option — so the default
 * theme's product page renders a complete buy experience rather than throwing on a missing `page.product`.
 * A live storefront product route always sets `page.product`, so it gets the real product.
 */
export function resolveProduct(context) {
    if (context.page.routeType === 'product' && context.page.product) {
        const product = context.page.product;
        return { product, variants: pickerVariants(product), isDemo: false };
    }
    // No route product: the builder / offline preview gets the demo so the default product template renders a
    // complete buy experience; the LIVE storefront gets `hidden` (a product section on a route that carries no
    // product has nothing honest to show — the section returns null rather than selling a fictional item).
    const demo = demoProduct(context.store.currency);
    const demoOk = allowDemo(context);
    return { product: demo, variants: pickerVariants(demo), isDemo: demoOk, hidden: !demoOk };
}
/**
 * Resolve the product the `featured-product` section should render: the PICKED product (the section's
 * `product` product-picker ref, host-resolved into `context.resolved.products`) when it resolves, else the
 * demo product — so a freshly-inserted section (nothing picked, or the builder preview where the picker is
 * not yet wired) still renders a complete Shrine buy experience. Mirrors {@link resolveProduct}, which reads
 * the ROUTE product instead.
 */
export function resolveProductFromRef(context, ref) {
    const picked = ref?.id ? context.resolved.products?.[ref.id] : undefined;
    if (picked) {
        return { product: picked, variants: variantsFromOptions(picked), isDemo: false, hidden: false };
    }
    // Unresolved: on the authoritative storefront the picked product is DRAFT / deleted — or nothing is picked
    // at all ⇒ hide (never serve demo to a real customer); in the builder (not ref-resolved) ⇒ demo.
    const hidden = !allowDemo(context);
    const demo = demoProduct(context.store.currency);
    return { product: demo, variants: variantsFromOptions(demo), isDemo: !hidden, hidden };
}
/**
 * Resolve the product a STANDALONE BUY-BOX sells — the `buy-box` section, which a seller drops on a landing
 * page where no product route exists (founder, 25/10/2026: "plusieurs sellers travaillent en landing pages
 * et n'utilisent pas du tout le composant produit principal").
 *
 * Two sources, in this order, and the order is the whole contract:
 *  1. the ROUTE product, when the section sits on a product template — so the same buy-box reused inside a
 *     product page keeps selling the product the shopper came for, and the seller never has to re-pick it
 *     per template;
 *  2. the section's OWN `product` picker, resolved by the host into `context.resolved.products`.
 *
 * Variants come from {@link pickerVariants}, never {@link variantsFromOptions}: a COD submit posts a
 * variant id, and an option-derived `${id}:${i}` is not a checkout-valid one. The host must therefore
 * resolve a picked buy-box product WITH its real variants (the page bundle does, for buy-box refs only).
 *
 * `hidden` keeps {@link resolveProduct}'s contract: on the authoritative storefront, a buy-box bound to
 * nothing — or to a DRAFT/deleted product — renders NOTHING rather than offering the house sample to a
 * real shopper. The builder still gets the demo, so a freshly inserted section is designable.
 */
export function resolveBuyboxProduct(context, ref) {
    if (context.page.routeType === 'product' && context.page.product) {
        const product = context.page.product;
        return { product, variants: pickerVariants(product), isDemo: false, hidden: false };
    }
    const picked = ref?.id ? context.resolved.products?.[ref.id] : undefined;
    if (picked) {
        return { product: picked, variants: pickerVariants(picked), isDemo: false, hidden: false };
    }
    const hidden = !allowDemo(context);
    const demo = demoProduct(context.store.currency);
    return { product: demo, variants: pickerVariants(demo), isDemo: !hidden, hidden };
}
/**
 * Resolve the collection a collection-page section should render: the route-bound collection when present,
 * else a demo collection (so the default collection document renders a complete page offline / in the
 * builder). Mirrors {@link resolveProduct}: on the LIVE storefront there is no demo — `hidden` is set and
 * the section must return null instead of naming a collection the store does not have.
 */
export function resolveCollection(context) {
    if (context.page.routeType === 'collection' && context.page.collection) {
        return { collection: context.page.collection, isDemo: false, hidden: false };
    }
    const demoOk = allowDemo(context);
    const list = demoCollectionsFor(context);
    const demo = list[0] ?? {
        id: 'demo-c',
        name: 'Collection',
        slug: 'collection',
        description: '',
        coverImage: null,
        productCount: 12,
    };
    return {
        collection: {
            ...demo,
            // La phrase maison ne sert que de repli : un catalogue de thème apporte SA description (« Attraper,
            // lâcher, faire rouler… » chez Pogo), et l'écraser rendait le bandeau générique dans une boutique
            // dont tout le reste est sur-mesure.
            description: demo.description ||
                'Notre sélection du moment — des pièces choisies avec soin, livrées partout au Maroc.',
        },
        isDemo: demoOk,
        hidden: !demoOk,
    };
}
/**
 * The product list a collection product-grid should render: the route-bound collection products when
 * present, else a fuller demo grid (the small demo set, cycled to ~12 cards with unique ids so the grid
 * reads as a populated collection) — but ONLY where demo content is allowed (builder / offline preview).
 * On the LIVE storefront an empty collection (or the whole catalog of a store with no products yet) returns
 * an EMPTY list, so the grid draws its empty state instead of a wall of sample products.
 */
export function resolveCollectionProducts(context, count = 12) {
    // Route-bound collection products (the authoritative storefront `collection` route fills these from the
    // backend, already ACTIVE-only + sorted + paginated) → render them, not the demo grid.
    if (context.page.routeType === 'collection' && (context.page.products?.length ?? 0) > 0) {
        return { products: [...context.page.products], isDemo: false };
    }
    if (!allowDemo(context))
        return { products: [], isDemo: false };
    const base = demoProductsFor(context);
    if (base.length === 0)
        return { products: [], isDemo: true };
    const products = [];
    for (let i = 0; products.length < count; i += 1) {
        const p = base[i % base.length];
        products.push(i < base.length ? p : { ...p, id: `${p.id}-${i}` });
    }
    return { products, isDemo: true };
}
/** A demo cart (3 lines off the demo grid) so the cart page/drawer render fully offline / in the builder. */
export function demoCart(currency, presetId) {
    const picks = demoProducts(currency, presetId).slice(0, 3);
    const lines = picks.map((product, i) => {
        const quantity = i === 0 ? 2 : 1;
        return {
            product,
            variantLabel: product.options?.[0]?.values?.[0]?.label ?? null,
            quantity,
            lineTotal: { amount: product.price.amount * quantity, currency: product.price.currency },
        };
    });
    const subtotal = lines.reduce((s, l) => s + l.lineTotal.amount, 0);
    const itemCount = lines.reduce((s, l) => s + l.quantity, 0);
    return { lines, itemCount, subtotal: { amount: subtotal, currency }, currency };
}
/** {@link demoCart} for THIS context — honours {@link RenderContext.demoMedia} like the grids do. */
function demoCartFor(context) {
    const cart = demoCart(context.store.currency, context.themeSettings?.presetId);
    if (demoMediaAllowed(context))
        return cart;
    return {
        ...cart,
        lines: cart.lines.map((l) => l.product.images.length ? { ...l, product: { ...l.product, images: [] } } : l),
    };
}
/**
 * Resolve the cart a cart-page / cart-drawer section should render. When the host injects a REAL cart on
 * `context.cart` (feature 0030 — the storefront /cart island threads its localStorage cart), render THAT;
 * otherwise fall back to the DEMO cart so the page still renders fully offline / in the builder preview.
 */
export function resolveCart(context) {
    if (context.cart)
        return { cart: context.cart, isDemo: false };
    // Live storefront with no cart injected: render the EMPTY cart, never the 3-line sample — a customer must
    // not see lines they did not add.
    if (!allowDemo(context)) {
        const currency = context.store.currency;
        return {
            cart: { lines: [], itemCount: 0, subtotal: { amount: 0, currency }, currency },
            isDemo: false,
        };
    }
    return { cart: demoCartFor(context), isDemo: true };
}
/**
 * A SAMPLE reviews band for the Add-section picker's preview pane — and for nowhere else (feature 0066).
 *
 * The picker's job is to show the component. `product-reviews` draws the shop's own data, so on a shop with
 * no reviews (or on a page that carries none) the pane showed a lone "0 reviews" line: a true sentence in
 * the canvas, an empty box in the picker, where the seller asked to see what she is about to add (founder,
 * 16/09/2026). This is what she sees there instead — the average, the five bars, dated cards with their
 * stars and their verified badge.
 *
 * **No invented customer SENTENCE.** Every item is a star with no words, which is neither a shortcut nor a
 * placeholder: it is the commonest real review there is (1 495 of the founder's own 1 535 imported rows),
 * the card is built for it, and the house rule is that a customer's words are never fabricated — a demo
 * quote read as a real one the moment it was screenshotted. Same reason there is no seller REPLY here, and
 * no photos: the demo never speaks for anybody.
 *
 * The dates are relative to NOW, so the sample never ages into a band of two-year-old reviews.
 */
export function demoReviews(now = Date.now()) {
    const DAY = 86_400_000;
    const ago = (days) => new Date(now - days * DAY).toISOString().slice(0, 10);
    const make = (id, rating, authorName, authorCountry, days, helpfulCount, verified = true) => ({
        id,
        rating,
        title: '',
        body: '',
        authorName,
        authorCountry,
        date: ago(days),
        verified,
        photos: [],
        reply: null,
        source: 'MANUAL',
        helpfulCount,
    });
    return {
        count: 128,
        average: 4.8,
        // Adds up to `count`, and lopsided the way a real shop's is — a flat histogram makes the bars look
        // like a chart nobody filled in.
        histogram: { '5': 104, '4': 18, '3': 4, '2': 1, '1': 1 },
        photoCount: 0,
        items: [
            make('demo-review-1', 5, 'Sara B.', 'MA', 3, 4),
            make('demo-review-2', 5, 'Yassine M.', 'MA', 9, 2),
            make('demo-review-3', 4, 'Imane K.', 'MA', 14, 1),
            make('demo-review-4', 5, 'Léa D.', 'FR', 23, 3),
            make('demo-review-5', 5, 'Omar T.', 'MA', 35, 0),
            make('demo-review-6', 4, 'Nadia S.', 'DZ', 48, 1, false),
        ],
    };
}
//# sourceMappingURL=product.js.map